import { useState, useEffect } from "react";
import "./demoMotion.css";
import "./demoThemes.css";
import "./demoComplete.css";
import { DemoBrowser, DemoFullFooter } from "./DemoFullParts";

const CODE = `function validateForm(data) {
  if (!data.email) return { ok: false };
  return { ok: true };
}`;

export default function AiCodeReviewDemo({ embed = false }) {
  const [score, setScore] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setScore(87), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`demo-root demo-code${embed ? " demo-root--embed" : ""}`}
      data-testid="portfolio-demo-ai-code-review"
      style={{
        "--accent": "#3b9eff",
        "--bg": "#0b0f14",
        "--muted": "#64748b",
        "--border": "#1e293b",
        "--card": "#111820",
        "--section-alt": "#0d1117",
      }}
    >
      <header className="demo-full__nav">
        <div style={{ fontWeight: 800, fontSize: "1rem" }}>
          Code<span style={{ color: "#3b9eff" }}>Lens</span>
        </div>
        <nav className="demo-full__nav-links">
          <span>기능</span>
          <span>교육기관</span>
          <span>요금</span>
          <span>도입 사례</span>
        </nav>
        <span className="demo-full__nav-cta">무료 체험</span>
      </header>

      <section className="demo-full__hero" style={{ background: "linear-gradient(180deg, rgba(59,158,255,.06), transparent)" }}>
        <div className="demo-full__hero-grid">
          <div>
            <span className="demo-full__eyebrow">AI Code Review Platform</span>
            <h1 style={{ color: "#e2e8f0" }}>
              코드 제출부터
              <br />
              AI 피드백까지 5분
            </h1>
            <p className="demo-full__hero-lead">
              교육기관·부트캠프를 위한 코드 리뷰 플랫폼. 강사 검토 시간 40% 절감,
              학습자는 즉시 개선 포인트를 받습니다.
            </p>
            <div className="demo-full__hero-btns">
              <span className="demo-full__btn demo-full__btn--primary">데모 신청</span>
              <span className="demo-full__btn demo-full__btn--secondary">기능 살펴보기</span>
            </div>
          </div>
          <DemoBrowser url="app.codelens.io/class/react-a" accent="#3b9eff">
            <div className="demo-code__split" style={{ minHeight: 260 }}>
              <div className="demo-code__editor">
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{CODE}</pre>
                <span className="demo-code__cursor demo-anim-blink" />
              </div>
              <div className="demo-code__review">
                <div className="demo-code__gauge">{score || "—"}</div>
                <div className="demo-code__issue demo-code__issue--warn">이메일 regex 검증 추가 권장</div>
                <div className="demo-code__issue demo-code__issue--ok">early return 패턴 적절</div>
              </div>
            </div>
          </DemoBrowser>
        </div>
      </section>

      <div className="demo-full__marquee">
        <div className="demo-full__marquee-track">
          {["패스트캠퍼스", "멋쟁이사자처럼", "SW마에스트로", "코드스테이츠", "KH정보교육원", "패스트캠퍼스", "멋쟁이사자처럼"].map((n, i) => (
            <span key={`${n}-${i}`}>{n}</span>
          ))}
        </div>
      </div>

      <section className="demo-full__section">
        <div className="demo-full__wrap">
          <div className="demo-full__head">
            <h2 style={{ color: "#e2e8f0" }}>교육 현장에 맞춘 기능</h2>
            <p>제출 → AI 분석 → 강사 피드백 → 리포트까지 한 플랫폼</p>
          </div>
          <div className="demo-full__grid3">
            {[
              { icon: "{}", t: "AI 자동 분석", d: "품질·구조·오류 패턴을 AI가 먼저 분석합니다." },
              { icon: "◎", t: "클래스 대시보드", d: "반별 제출 현황·점수 추이를 한 화면에서 확인합니다." },
              { icon: "✓", t: "권한별 접근", d: "학생·강사·관리자 역할에 맞는 화면을 제공합니다." },
            ].map((f) => (
              <article key={f.t} className="demo-full__card">
                <div className="demo-full__card-icon">{f.icon}</div>
                <h3 style={{ color: "#e2e8f0" }}>{f.t}</h3>
                <p>{f.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="demo-full__section demo-full__section--alt">
        <div className="demo-full__wrap">
          <div className="demo-full__head">
            <h2 style={{ color: "#e2e8f0" }}>요금 플랜</h2>
          </div>
          <div className="demo-full__pricing">
            {[
              { name: "Starter", price: "₩99,000", items: ["클래스 2개", "학생 50명", "AI 리뷰 500회/월"] },
              { name: "Pro", price: "₩249,000", featured: true, items: ["클래스 10개", "학생 300명", "AI 리뷰 무제한", "강사 대시보드"] },
              { name: "Enterprise", price: "문의", items: ["무제한 클래스", "SSO 연동", "전담 지원", "API 접근"] },
            ].map((p) => (
              <div key={p.name} className={`demo-full__price-card${p.featured ? " demo-full__price-card--featured" : ""}`}>
                <h4 style={{ color: "#e2e8f0" }}>{p.name}</h4>
                <div className="amount" style={{ color: "#3b9eff" }}>{p.price}</div>
                <span style={{ fontSize: "0.72rem", color: "#64748b" }}>/ 월</span>
                <ul>{p.items.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="demo-full__section">
        <div className="demo-full__wrap">
          <div className="demo-full__statbar">
            {[
              { v: "5분", l: "평균 피드백" },
              { v: "86%", l: "제출률" },
              { v: "-40%", l: "강사 검토 시간" },
              { v: "12", l: "지원 언어" },
            ].map((s) => (
              <div key={s.l} className="demo-full__statbar-item">
                <strong>{s.v}</strong>
                <span>{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "0 1.5rem 3rem", maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ margin: "0 0 0.5rem", color: "#e2e8f0", fontSize: "1.5rem" }}>교육기관 맞춤 도입 상담</h2>
        <p style={{ margin: "0 0 1.25rem", color: "#64748b", fontSize: "0.88rem" }}>클래스 규모에 맞는 플랜을 안내해 드립니다.</p>
        <span className="demo-full__btn demo-full__btn--primary">도입 문의하기</span>
      </div>

      <DemoFullFooter
        brand="CodeLens"
        desc="교육기관·부트캠프를 위한 AI 코드 리뷰 플랫폼"
        columns={[
          { title: "제품", links: ["AI 리뷰", "클래스 관리", "리포트", "API"] },
          { title: "지원", links: ["도입 가이드", "보안", "FAQ", "문서"] },
          { title: "연락", links: ["edu@codelens.io", "채널톡", "서울 판교"] },
        ]}
        copy="© 2026 CodeLens Inc. All rights reserved."
      />
    </div>
  );
}
